import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';

// Import the new ThemeProvider
import { ThemeProvider } from './ThemeProvider';

// Main app components
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import TradesSection from './components/TradesSection';
import PricingSection from './components/PricingSection';
import Footer from './components/Footer';

// Pages
import ProfilePage from './pages/ProfilePage';
import DashboardPage from './pages/DashboardPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Legal Pages
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RiskDisclosurePage from './pages/RiskDisclosurePage';

// Components
import PaymentSuccess from './components/PaymentSuccess';

// Services
import authService from './services/authService';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Auth guard component - now redirects to home with auth param
const AuthGuard = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    // Redirect to home with login modal trigger
    return <Navigate to="/?auth=login" replace />;
  }
  
  return children;
};

// Main homepage component
const Homepage = () => (
  <>
    <Header />
    <HeroSection />
    <TradesSection />
    <PricingSection />
    <Footer />
  </>
);

// Layout with header and footer
const DashboardLayout = ({ children }) => (
  <>
    <Header />
    {children}
    <Footer />
  </>
);

// Legal pages layout
const LegalLayout = ({ children }) => (
  <>
    <Header />
    {children}
    <Footer />
  </>
);

function XAUPilotApp() {
  return (
    <ThemeProvider>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Main Pages */}
          <Route path="/" element={<Homepage />} />
          
          {/* Redirect old login/register routes to home with modal */}
          <Route path="/login" element={<Navigate to="/?auth=login" replace />} />
          <Route path="/register" element={<Navigate to="/?auth=register" replace />} />
          
          {/* Password Reset Pages - these still need separate pages */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Legal Pages */}
          <Route path="/terms" element={
            <LegalLayout>
              <TermsOfServicePage />
            </LegalLayout>
          } />
          <Route path="/privacy" element={
            <LegalLayout>
              <PrivacyPolicyPage />
            </LegalLayout>
          } />
          <Route path="/risk-disclosure" element={
            <LegalLayout>
              <RiskDisclosurePage />
            </LegalLayout>
          } />
          
          {/* Payment Success Route */}
          <Route path="/payment/success" element={
            <AuthGuard>
              <DashboardLayout>
                <PaymentSuccess />
              </DashboardLayout>
            </AuthGuard>
          } />
          
          {/* Protected User Pages */}
          <Route path="/profile" element={
            <AuthGuard>
              <DashboardLayout>
                <ProfilePage />
              </DashboardLayout>
            </AuthGuard>
          } />
          <Route path="/dashboard" element={
            <AuthGuard>
              <DashboardLayout>
                <DashboardPage />
              </DashboardLayout>
            </AuthGuard>
          } />
          <Route path="/subscription" element={
            <AuthGuard>
              <DashboardLayout>
                <SubscriptionPage />
              </DashboardLayout>
            </AuthGuard>
          } />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default XAUPilotApp;